import { FC, useEffect, useRef, useState } from 'react';
import { CareMapDrawerPage } from '../components/CareMapDrawerPage';
import { IContactMethod, ISupportGroupKey, ISupportGroupMember } from '../caremap/ICareMap';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CareMapState } from '../caremap/CareMapState';
import { useSubscription } from '@meraki-internal/state';
import { TextField } from '../components/TextField';
import { SupportGroupDataProvider } from '../caremap/SupportGroupDataProvider';
import { SupportGroupIconProvider } from '../caremap/SupportGroupIconProvider';
import { RoleSelectField } from '../components/RoleSelectField';
import { IonButton } from '@ionic/react';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useDeleteMember } from './useDeleteMember';
import { CareMapDrawerState } from '../caremap/CareMapDrawerState';
import { MixPanelEventEmitter as MixpanelService } from '../metrics/MixPanelEventEmitter';
import { DeleteMemberButton } from './DeleteMemberButton';
import { CustomTypeAddedMetaBuilder } from '../metrics/CustomTypeAddedMetaBuilder';
import { ILabelKey } from '../config/LabelProvider';
import { EmailField, IEmailContactMethod } from '../contact-methods/EmailField';
import { IPhoneContactMethod, PhoneField } from '../contact-methods/PhoneField';
import { IAddFromContactsButtonResult, AddFromContactsButton } from '../contacts/AddFromContactsButton';
import { LinkedMemberEmailField } from '../caremap/LinkedMemberEmailField';
import { useConfirmLinkThenSave } from '../caremap/useConfirmLinkThenSave';

export interface ISupportGroupMemberEditProps {
    supportGroupKey: ISupportGroupKey;
    memberId?: string;
    autoSelectFromContacts?: boolean;
};

export const SupportGroupMemberEdit: FC<ISupportGroupMemberEditProps> = ({ supportGroupKey, memberId, autoSelectFromContacts }) => {
    const history = useInstance(HistoryViewModel);
    const tracking = useInstance(MixpanelService);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const drawerState = useInstance(CareMapDrawerState);

    const [addedFromContacts, setAddedFromContacts] = useState<boolean>(false);

    useEffect(() => {
        drawerState.setIsMaskingFocusedCareMap(true);

        return () => {
            drawerState.setIsMaskingFocusedCareMap(false);
        };
    }, [supportGroupKey, drawerState]);

    const addFromContactsRef = useRef<HTMLIonButtonElement>(null);
    useEffect(() => {
        if(autoSelectFromContacts && addFromContactsRef.current) {
            addFromContactsRef.current.click();
        }
    }, [addFromContactsRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirmLinkThenSave = useConfirmLinkThenSave();

    const dataProvider = useInstance(SupportGroupDataProvider);
    const roleOptions = dataProvider.getSupportGroupMemberSelectOptions(supportGroupKey);

    const iconProvider = useInstance(SupportGroupIconProvider);

    const isAdding = (!memberId);

    let initialMember: ISupportGroupMember | undefined;
    if (isAdding) {
        initialMember = careMapState.getNewSupportGroupMember({ supportGroupKey });
    } else {
        initialMember = careMapState.getSupportGroupMember({ supportGroupKey, memberId });
    }

    // initializing with ! here because we don't need to consider undefined after guard just below
    const [member, setMember] = useState(initialMember!);

    const onDelete = useDeleteMember({ supportGroupKey, memberId: memberId!, onDelete: () => {
        // need to go back twice to skip through read-only page
        history.back({ pages: 2});
    }});

    if (!careMapState.state || !member) {
        return null;
    }

    const isMe = careMapState.isMySupportMember(member);

    const onCancel = () => {
        history.back();
    };

    const actuallySave = () => {
        careMapState[isAdding ? 'addSupportGroupMember' : 'updateSupportGroupMember'](supportGroupKey, member);
        tracking.track(`Care Member ${isAdding ? 'Added': 'Edited'}`, () => ({
            'Added From Contacts': addedFromContacts,
            '# of Contact Methods': careMapState.cleanupContactMethods(member.contactMethods).length
        }));
        history.back();
    };

    const onSave = () => {
        confirmLinkThenSave({
            linkedUserId: member.linkedUserId,
            email: careMapState.getContactMethodOrDefault(member, 'email').value,
            name: member.name,
            isSelf: isMe,
            memberNoun: (labels.memberTitle || 'member').toLowerCase(),
            save: actuallySave
        });
    };

    // Unlink: clear the email contact method and persist via the normal save path so the server
    // drops linkedUserId. Also clear linkedUserId optimistically so the form immediately flips
    // back to the editable EmailField rather than waiting for the post-save refresh.
    const onUnlink = () => {
        const contactMethods = member.contactMethods.filter(m => m.type !== 'email');
        const unlinked = { ...member, contactMethods, linkedUserId: undefined };
        setMember(unlinked);
        careMapState.updateSupportGroupMember(supportGroupKey, unlinked);
    };

    const isValid = Boolean(member.name?.trim() || member.role);

    const labels = careMapState.getSupportGroupLabels(supportGroupKey);
    const color = careMapState.getSupportGroupColor(supportGroupKey);
    const { MemberIcon } = iconProvider.getSupportGroupIcons(supportGroupKey);

    /* const onMethodsChanged = (contactMethods: IContactMethod[]) => {
        setMember(m => ({ ...m, contactMethods }));
    }; */

    const contactMethodChanged = (method: IContactMethod) => {
        const copy = [ ...member.contactMethods ];
        const idx = copy.findIndex(m => m.type === method.type);

        if (idx > -1) {
            copy.splice(idx, 1, method);
        } else {
            copy.push(method);
        }

        setMember(m => ({ ...m, contactMethods: copy }));
    };

    const buttons = <>
        <IonButton data-type="save-button" disabled={!isValid} onClick={onSave}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={onCancel}>Cancel</IonButton>
    </>;

    const onRoleChangedMetrics = (updates: { roleId?: string; role?: string; }) => {
        if (updates.role && !updates.roleId){
            tracking.trackWithDebounce({
                event: 'Custom Type Added',
                getContext: () => new CustomTypeAddedMetaBuilder()
                    .origin('caremap')
                    .supportGroup(supportGroupKey)
                    .field({ field: 'role', fieldLabelKey: `SUPPORT_GROUP__${supportGroupKey.toUpperCase()}__ROLE` as ILabelKey, value: updates.role! })
                    .build()
            });
        }
    };

    const onRoleChanged = (updates: { roleId?: string; role?: string; }) => {
        setMember(m => ({ ...m, ...updates }));
        onRoleChangedMetrics(updates);
    };

    const roleField = (
        <div style={{flexGrow: 1}}>
            <RoleSelectField
                roleId={member.roleId}
                role={member.role}
                label={labels.roleLabel}
                placeholder={labels.rolePlaceholder}
                options={roleOptions}
                onChange={onRoleChanged}
            />
        </div>
    );

    const nameField = (
        <TextField
            name="name"
            label={labels.memberNameLabel}
            value={member.name}
            autocapitalize="words"
            onInput={name => setMember(m => ({ ...m, name }))}
        />
    );

    const showNameFirst = careMapState.shouldShowNameFirst(supportGroupKey);

    return (
        <CareMapDrawerPage
            title={isMe ? 'Edit Your Info' : `${isAdding ? 'Add' : 'Edit'} ${labels.memberTitle}`}
            hasBack={true}
            buttons={buttons}
            data-type="member-edit"
            data-member-id={memberId}
        >
            <AddFromContactsButton
                btnRef={addFromContactsRef}
                onContactSelected={((patch: IAddFromContactsButtonResult) => {
                    setAddedFromContacts(true);
                    const name = `${patch.firstName}${patch.lastName ? ' ' : ''}${patch.lastName}`;
                    delete patch.firstName;
                    delete patch.lastName;
                    setMember(m => ({ ...member, ...patch, name }));
                })}
            />

            <div style={{display: 'flex', flexDirection: 'column', gap: 20}}>

                <div style={{display: 'flex', alignItems: 'center', width: '100%', gap: 20}}>
                    <div style={{flexShrink: 0}}>
                        <MemberIcon color={color} size="xlarge" shape="round" />
                    </div>
                    {showNameFirst ? nameField : roleField}
                </div>

                {showNameFirst ? roleField : nameField}

                <TextField
                    name="organization"
                    label="Organization"
                    value={member.organization}
                    autocapitalize="words"
                    onInput={organization => setMember(m => ({ ...m, organization }))}
                />

                <TextField
                    name="address"
                    label="Address"
                    value={member.address}
                    autocapitalize="words"
                    onInput={address => setMember(m => ({ ...m, address }))}
                />

                {member.linkedUserId ? (
                    <LinkedMemberEmailField
                        email={careMapState.getContactMethodOrDefault(member, 'email').value}
                        linkedToName={member.name || 'this person'}
                        onUnlink={onUnlink}
                        isSelf={isMe}
                    />
                ) : (
                    <EmailField
                        methodChanged={contactMethodChanged}
                        method={careMapState.getContactMethodOrDefault(member, 'email') as IEmailContactMethod}
                    />
                )}
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(member, 'mobile-phone') as IPhoneContactMethod}
                />
                <PhoneField
                    methodChanged={contactMethodChanged}
                    method={careMapState.getContactMethodOrDefault(member, 'work-phone') as IPhoneContactMethod}
                />
            </div>

            {!isAdding &&
                <DeleteMemberButton onDelete={onDelete} memberLabel={labels.memberTitle} />
            }

        </CareMapDrawerPage>
    );
};
